import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';

type Settings = Record<string, unknown>;

export function AdminSettingsPage() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Settings>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ settings: Settings }>('/admin/settings');
        setSettings(d.settings);
      } catch { /* */ }
      finally { setIsLoading(false); }
    })();
  }, []);

  const update = (key: string, value: unknown) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const d = await apiFetch<{ settings: Settings }>('/admin/settings', {
        method: 'PUT', body: JSON.stringify(settings),
      });
      setSettings(d.settings);
      toast('success', 'Platform settings saved');
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  if (isLoading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  const s = (key: string) => (settings[key] ?? '') as string;
  const n = (key: string) => (settings[key] ?? 0) as number;
  const a = (key: string) => (settings[key] ?? []) as string[];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Platform Settings</h2>
          <p className="text-sm text-gray-500">Global defaults for all accounts</p>
        </div>
        <button onClick={save} disabled={isSaving}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
          {isSaving ? 'Saving...' : 'Save All'}
        </button>
      </div>

      {/* Branding & Email */}
      <Section title="Email & Branding">
        <Field label="From Email" value={s('email.from_address')} onChange={v => update('email.from_address', v)} placeholder="noreply@97display.com" />
        <Field label="From Name" value={s('email.from_name')} onChange={v => update('email.from_name', v)} placeholder="97 Display" />
        <Field label="Brand Name (email headers)" value={s('branding.name')} onChange={v => update('branding.name', v)} placeholder="CRM98" />
        <Field label="Footer Text" value={s('branding.footer')} onChange={v => update('branding.footer', v)} placeholder="Powered by 97 Display" />
        <Field label="Daily Email Cap" value={String(n('email.daily_cap'))} onChange={v => update('email.daily_cap', Number(v))} type="number" />
      </Section>

      {/* Default Message Templates */}
      <Section title="Default Message Templates">
        <p className="text-xs text-gray-400 mb-2">These are used when an account hasn't configured their own. Merge fields: <code className="bg-gray-100 px-1 rounded">{'{{lead_name}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{lead_date}}'}</code></p>
        <TextArea label="Default SMS Template" value={s('templates.sms_default')} onChange={v => update('templates.sms_default', v)} rows={3} maxLength={320}
          hint={`${s('templates.sms_default').length}/320 characters`} />
        <TextArea label="Default Voice Template" value={s('templates.voice_default')} onChange={v => update('templates.voice_default', v)} rows={3}
          hint="Spoken by the automated voice call system" />
        <TextArea label="Drip Fallback Subject" value={s('templates.drip_fallback_subject')} onChange={v => update('templates.drip_fallback_subject', v)} rows={1} />
        <TextArea label="Drip Fallback Body (HTML)" value={s('templates.drip_fallback_body')} onChange={v => update('templates.drip_fallback_body', v)} rows={3}
          hint="Used when a drip step has no template assigned" />
      </Section>

      {/* Account Defaults */}
      <Section title="New Account Defaults">
        <Field label="Trial Duration (days)" value={String(n('defaults.trial_duration_days'))} onChange={v => update('defaults.trial_duration_days', Number(v))} type="number" />
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Default Notification Channels</label>
          <div className="flex gap-3">
            {['email', 'sms', 'voice'].map(ch => (
              <label key={ch} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input type="checkbox" checked={a('defaults.channels_enabled').includes(ch)}
                  onChange={e => {
                    const current = a('defaults.channels_enabled');
                    update('defaults.channels_enabled', e.target.checked ? [...current, ch] : current.filter(c => c !== ch));
                  }}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                {ch}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Quiet Hours Timezone" value={s('defaults.quiet_hours_timezone')} onChange={v => update('defaults.quiet_hours_timezone', v)} />
          <Field label="Quiet Hours Start" value={s('defaults.quiet_hours_start')} onChange={v => update('defaults.quiet_hours_start', v)} type="time" />
          <Field label="Quiet Hours End" value={s('defaults.quiet_hours_end')} onChange={v => update('defaults.quiet_hours_end', v)} type="time" />
        </div>
      </Section>

      {/* Limits */}
      <Section title="Limits & Security">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Impersonation TTL (minutes)" value={String(n('limits.impersonation_ttl_minutes'))} onChange={v => update('limits.impersonation_ttl_minutes', Number(v))} type="number" />
        </div>
      </Section>

      {/* Bottom save */}
      <button onClick={save} disabled={isSaving}
        className="w-full bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
        {isSaving ? 'Saving...' : 'Save Platform Settings'}
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, maxLength, hint }: {
  label: string; value: string; onChange: (v: string) => void; rows?: number; maxLength?: number; hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} maxLength={maxLength}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}
